import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { EmployeeService, EmployeeReferences } from '../../services/employee.service';
import { RoleService } from '../../services/role.service';
import { DesignationService } from '../../services/designation.service';
import { CategoryService } from '../../services/category.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { OnboardingService } from '../../services/onboarding.service';
import { OnboardingRecord } from '../../models/onboarding.model';
import { Employee } from '../../models/employee.model';
import { Role } from '../../models/role.model';
import { Designation } from '../../models/designation.model';
import { Category } from '../../models/category.model';
import { ServiceGroup } from '../../models/service-group.model';
import { EmployeeFormDialogComponent, EmployeeSavedEvent } from '../employee-form-dialog/employee-form-dialog.component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface DropdownOption {
  label: string;
  value: number | string;
}

@Component({
  selector: 'app-employee-management',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, Button,
    Select, Tooltip, ConfirmDialog, Dialog,
    EmployeeFormDialogComponent
  ],
  templateUrl: './employee-management.component.html',
  styleUrl: './employee-management.component.scss'
})
export class EmployeeManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  employees: Employee[] = [];
  filteredEmployees: Employee[] = [];
  roles: Role[] = [];
  designations: Designation[] = [];
  categories: Category[] = [];
  serviceGroups: ServiceGroup[] = [];
  loading: boolean = true;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';
  selectedRoleFilter: string | null = null;
  selectedDesignationFilter: string | null = null;
  selectedCategoryFilter: string | null = null;
  selectedServiceGroupFilter: string | null = null;

  // Pagination
  rows = 50;
  first = 0;
  totalRecords = 0;
  rowsPerPageOptions = [5, 10, 20, 50, 100];

  // Employee form dialog (shared component)
  displayEmployeeFormDialog = false;
  employeeToEdit: Employee | null = null;

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  roleOptions: DropdownOption[] = [];
  designationOptions: DropdownOption[] = [];
  categoryOptions: DropdownOption[] = [];
  serviceGroupOptions: DropdownOption[] = [];
  // Onboarding status per employee: 'none' | 'InProgress' | 'Completed'
  onboardingStatusMap: Record<string, string> = {};

  /** Stats for cards (shared stats-cards pattern). */
  get totalEmployeesCount(): number {
    return this.employees.length;
  }
  get activeEmployeesCount(): number {
    return this.employees.filter(e => e.isActive).length;
  }
  get inactiveEmployeesCount(): number {
    return this.employees.filter(e => !e.isActive).length;
  }

  constructor(
    private employeeService: EmployeeService,
    private roleService: RoleService,
    private designationService: DesignationService,
    private categoryService: CategoryService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    private router: Router,
    public pageHeaderService: PageHeaderSearchActionService,
    private onboardingService: OnboardingService
  ) { }

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by name or email...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Employee');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadData();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.showAddDialog();
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportToExcel();
    });
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!(user?.isAdmin || user?.isPc));
      this.applySearch();
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData() {
    this.loading = true;
    forkJoin({
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(error => {
          console.error('Error loading employees:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load employees. Please refresh the page or try again later.'
          });
          return of([] as Employee[]);
        })
      ),
      roles: this.roleService.getAllRoles().pipe(
        catchError(error => {
          console.error('Error loading roles:', error);
          return of([] as Role[]);
        })
      ),
      designations: this.designationService.getAllDesignations().pipe(
        catchError(error => {
          console.error('Error loading designations:', error);
          return of([] as Designation[]);
        })
      ),
      categories: this.categoryService.getAllCategories().pipe(
        catchError(error => {
          console.error('Error loading categories:', error);
          return of([] as Category[]);
        })
      ),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(
        catchError(error => {
          console.error('Error loading service groups:', error);
          return of([] as ServiceGroup[]);
        })
      ),
      onboardingRecords: this.onboardingService.getAllRecords().pipe(
        catchError(() => of([] as OnboardingRecord[]))
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.employees = data.employees || [];
        this.roles = (data.roles || []).filter(r => r.isActive);
        this.designations = (data.designations || []).filter(d => d.isActive);
        this.categories = (data.categories || []).filter(c => c.isActive);
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);
        this.roleOptions = this.roles.map(r => ({
          label: r.title,
          value: r.id
        }));
        this.designationOptions = this.designations.map(d => ({
          label: d.name,
          value: d.id
        }));
        this.categoryOptions = this.categories.map(c => ({
          label: c.name,
          value: c.id
        }));
        this.serviceGroupOptions = this.serviceGroups.map(sg => ({
          label: sg.name,
          value: sg.id
        }));
        // Build onboarding status map
        this.onboardingStatusMap = {};
        for (const record of (data.onboardingRecords || [])) {
          const existing = this.onboardingStatusMap[record.employeeId];
          // Prefer 'InProgress' over 'Completed' if multiple records exist
          if (!existing || record.status === 'InProgress') {
            this.onboardingStatusMap[record.employeeId] = record.status;
          }
        }
        // Reapply filters to preserve filtered state
        this.applySearch();
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.employees];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name?.toLowerCase().includes(searchLower) ||
        emp.email?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(emp => emp.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(emp => emp.isActive === false);
      }
    }

    // Apply role filter (employee matches if they have the selected role in roleIds or as primary roleId)
    if (this.selectedRoleFilter !== null) {
      filtered = filtered.filter(emp =>
        (emp.roleIds && emp.roleIds.includes(this.selectedRoleFilter!)) || emp.roleId === this.selectedRoleFilter
      );
    }

    // Apply designation filter
    if (this.selectedDesignationFilter !== null) {
      filtered = filtered.filter(emp => emp.designationId === this.selectedDesignationFilter);
    }

    // Apply category filter
    if (this.selectedCategoryFilter !== null) {
      filtered = filtered.filter(emp => emp.categoryId === this.selectedCategoryFilter);
    }

    // Apply service group filter (employee can belong to multiple service groups)
    if (this.selectedServiceGroupFilter !== null) {
      filtered = filtered.filter(emp =>
        emp.serviceGroupId === this.selectedServiceGroupFilter ||
        (emp.serviceGroupIds && emp.serviceGroupIds.includes(this.selectedServiceGroupFilter!))
      );
    }

    this.filteredEmployees = filtered;
    this.totalRecords = this.filteredEmployees.length;
    this.first = 0;
    this.pageHeaderService.setExportDisabled(this.filteredEmployees.length === 0);
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applySearch();
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  onRoleFilterChange() {
    this.applySearch();
  }

  onDesignationFilterChange() {
    this.applySearch();
  }

  onCategoryFilterChange() {
    this.applySearch();
  }

  onServiceGroupFilterChange() {
    this.applySearch();
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active'; // Reset to default 'active' view
    this.selectedRoleFilter = null;
    this.selectedDesignationFilter = null;
    this.selectedCategoryFilter = null;
    this.selectedServiceGroupFilter = null;
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() ||
      (this.selectedStatusFilter !== null && this.selectedStatusFilter !== 'active') ||
      this.selectedRoleFilter ||
      this.selectedDesignationFilter ||
      this.selectedCategoryFilter ||
      this.selectedServiceGroupFilter);
  }

  showAddDialog(): void {
    this.employeeToEdit = null;
    this.displayEmployeeFormDialog = true;
  }

  showEditDialog(employee: Employee): void {
    this.employeeToEdit = employee;
    this.displayEmployeeFormDialog = true;
  }

  onEmployeeSaved(event: EmployeeSavedEvent): void {
    this.displayEmployeeFormDialog = false;
    if (event.isNew) {
      this.employees = [...this.employees, event.employee];

      // The form dialog creates the onboarding record itself when the checkbox is ticked; this
      // only reports it, so the user knows a checklist was started from this screen too.
      if (event.startOnboarding) {
        this.messageService.add({
          severity: 'success',
          summary: 'Onboarding Started',
          detail: `Onboarding checklist created for ${event.employee.name}.`
        });
      }
    } else if (event.startOnboarding) {
      // Started from the edit form — reload so the status map knows about the new record.
      this.messageService.add({
        severity: 'success',
        summary: 'Onboarding Started',
        detail: `Onboarding checklist created for ${event.employee.name}.`
      });
      this.loadData();
      return;
    } else {
      const index = this.employees.findIndex(e => e.id === event.employee.id);
      if (index !== -1) {
        this.employees = [...this.employees];
        this.employees[index] = event.employee;
      }
    }
    this.applySearch();
  }

  onEmployeeFormCancel(): void {
    this.displayEmployeeFormDialog = false;
    this.employeeToEdit = null;
  }

  /**
   * Admin-only gate for the permanent delete action. P&C can add and edit employees, but removing
   * a record outright is irreversible, so it stays with admins. The API enforces this too — hiding
   * the icon is for clarity, not security.
   */
  get canDeleteEmployees(): boolean {
    return !!this.authService.getUser()?.isAdmin;
  }

  /**
   * Whether the employee open in the edit dialog already has an onboarding record — drives whether
   * the checklist checkbox is offered or shown ticked-and-disabled.
   */
  get editingEmployeeHasOnboarding(): boolean {
    const id = this.employeeToEdit?.id;
    return !!id && !!this.onboardingStatusMap[id];
  }

  // ===== Permanent delete (inactive employees only) =====

  deleteDialogVisible = false;
  deleteTarget: EmployeeReferences | null = null;
  deleteRefsLoading = false;
  deleting = false;

  /**
   * Opens the delete dialog and loads what references this employee, so the user sees what would
   * be lost instead of a bare confirmation. The API refuses the delete independently — this is to
   * inform the decision, not to guard it.
   */
  confirmDelete(employee: Employee): void {
    this.deleteTarget = null;
    this.deleteRefsLoading = true;
    this.deleteDialogVisible = true;

    this.employeeService.getEmployeeReferences(employee.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (refs) => {
          this.deleteTarget = refs;
          this.deleteRefsLoading = false;
        },
        error: () => {
          this.deleteRefsLoading = false;
          this.deleteDialogVisible = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Could not check what references this employee. Please try again.'
          });
        }
      });
  }

  performDelete(): void {
    if (!this.deleteTarget || this.deleting) return;

    const name = this.deleteTarget.employeeName;
    this.deleting = true;

    this.employeeService.deleteEmployee(this.deleteTarget.employeeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting = false;
          this.deleteDialogVisible = false;
          this.deleteTarget = null;
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: `${name} has been permanently deleted.`
          });
          this.loadData();
        },
        error: (err) => {
          this.deleting = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Cannot Delete',
            detail: err?.error?.message || 'This employee could not be deleted.',
            life: 8000
          });
        }
      });
  }

  toggleActiveStatus(employee: Employee) {
    const nowActive = !employee.isActive;
    // Quick toggle from the list has no date picker, so deactivating here stamps today and
    // reactivating clears it. Use the edit dialog to backdate someone who left earlier.
    const updatedEmployee = {
      ...employee,
      isActive: nowActive,
      inactiveDate: nowActive ? undefined : (employee.inactiveDate ?? this.todayIsoDate())
    };
    this.employeeService.updateEmployee(employee.id, updatedEmployee).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Employee ${updatedEmployee.isActive ? 'activated' : 'deactivated'} successfully`
        });
        const index = this.employees.findIndex(e => e.id === employee.id);
        if (index !== -1) {
          this.employees = [...this.employees];
          this.employees[index] = {
            ...this.employees[index],
            isActive: updatedEmployee.isActive,
            inactiveDate: updatedEmployee.inactiveDate
          };
        }
        this.applySearch();
      },
      error: (error) => {
        console.error('Error updating employee status:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update employee status'
        });
      }
    });
  }

  /** Today as 'YYYY-MM-DD' from local parts — toISOString would shift the day in some timezones. */
  private todayIsoDate(): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }

  getRoleName(roleId: string): string {
    const role = this.roles.find(r => r.id === roleId);
    return role ? role.title : 'Unknown';
  }

  getEmployeeRoleDisplay(employee: Employee): string {
    if (employee.roleTitles && employee.roleTitles.length > 0) {
      return employee.roleTitles.join(', ');
    }
    return employee.roleTitle || this.getRoleName(employee.roleId);
  }

  getServiceGroupName(serviceGroupId: string): string {
    const serviceGroup = this.serviceGroups.find(sg => sg.id === serviceGroupId);
    return serviceGroup ? serviceGroup.name : 'Unknown';
  }

  /** Display text for employee's service group(s) - multiple or single. */
  getServiceGroupDisplay(employee: Employee): string {
    if (employee.serviceGroupNames && employee.serviceGroupNames.length > 0) {
      return employee.serviceGroupNames.join(', ');
    }
    if (employee.serviceGroupIds && employee.serviceGroupIds.length > 0) {
      return employee.serviceGroupIds.map(id => this.getServiceGroupName(id)).join(', ');
    }
    return employee.serviceGroupName || this.getServiceGroupName(employee.serviceGroupId) || '–';
  }

  getRoleSeverity(roleId: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const role = this.roles.find(r => r.id === roleId);
    if (!role) return 'contrast';

    const title = role.title.toLowerCase();
    if (title.includes('project manager') && !title.includes('senior')) {
      return 'danger';
    } else if (title.includes('developer') && !title.includes('intern')) {
      return 'success';
    } else if (title.includes('qa') && !title.includes('intern')) {
      return 'info';
    } else if (title.includes('business analyst') && !title.includes('intern')) {
      return 'warn';
    } else if (title.includes('intern')) {
      return 'secondary';
    }
    return 'contrast';
  }

  getOnboardingStatus(employee: Employee): string {
    return this.onboardingStatusMap[employee.id] || 'none';
  }

  startOnboarding(employee: Employee) {
    const status = this.getOnboardingStatus(employee);
    if (status === 'InProgress') {
      // Navigate to onboarding list
      this.router.navigate(['/onboarding/employees']);
      return;
    }
    if (status === 'Completed') {
      return; // No action for completed
    }

    this.confirmationService.confirm({
      message: `Start onboarding for "${employee.name}"? This will create onboarding tasks and welcome pack items.`,
      header: 'Start Onboarding',
      icon: 'pi pi-user-plus',
      accept: () => {
        this.onboardingService.createRecord({ employeeId: employee.id })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Onboarding Started',
                detail: `Onboarding started for ${employee.name}`
              });
              this.loadData(); // Reload to update status
            },
            error: (err) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: err.error?.details || err.error?.title || 'Failed to start onboarding'
              });
            }
          });
      }
    });
  }

  exportToExcel() {
    if (!this.filteredEmployees || this.filteredEmployees.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No employees to export'
      });
      return;
    }

    try {
      // Prepare data for Excel export
      const exportData = this.filteredEmployees.map(emp => ({
        'Name': emp.name || '',
        'Email': emp.email || '',
        'Role': this.getEmployeeRoleDisplay(emp),
        'Designation': emp.designationName || '',
        'Category': emp.categoryName || '',
        'Service Group': this.getServiceGroupDisplay(emp),
        'Status': emp.isActive ? 'Active' : 'Inactive'
      }));

      // Create worksheet
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 25 }, // Name
        { wch: 30 }, // Email
        { wch: 20 }, // Role
        { wch: 20 }, // Designation
        { wch: 20 }, // Category
        { wch: 15 }, // Service Group
        { wch: 12 }  // Status
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Employees': worksheet },
        SheetNames: ['Employees']
      };

      // Generate Excel file
      const excelBuffer: any = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      // Create blob and save file
      const data: Blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const fileName = `Employee_Management_${this.formatDate(new Date())}.xlsx`;
      saveAs(data, fileName);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Employee data exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export employee data to Excel'
      });
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }
}

